/**
 * Sidebar pill that pre-warms Cowork-scoped MCP servers (e.g. Atlassian).
 *
 * Without this, the first task creation blocks behind the mcp-remote OAuth
 * popup, which is confusing UX. Clicking "Connect" up front triggers the
 * same flow before any task exists — the dev host opens its browser to the
 * Atlassian consent screen, and once tokens are cached, the pill flips to
 * "Connected".
 *
 * Renders one pill per server returned by `/api/mcp/cowork-status`.
 */

import { LitElement, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { registerMcpRenderers } from "../tools/mcp-renderer.js";

interface ServerStatus {
	id: string;
	label: string;
	connected: boolean;
	toolNames: string[];
}

@customElement("mcp-connector")
export class McpConnector extends LitElement {
	createRenderRoot() {
		return this;
	}

	@state() private servers: ServerStatus[] = [];
	@state() private connectingId: string | null = null;

	private readonly onFocus = () => void this.refresh();
	private readonly onVisibility = () => {
		if (document.visibilityState === "visible") void this.refresh();
	};

	connectedCallback(): void {
		super.connectedCallback();
		void this.refresh();
		window.addEventListener("focus", this.onFocus);
		document.addEventListener("visibilitychange", this.onVisibility);
	}

	disconnectedCallback(): void {
		super.disconnectedCallback();
		window.removeEventListener("focus", this.onFocus);
		document.removeEventListener("visibilitychange", this.onVisibility);
	}

	private async refresh(): Promise<void> {
		try {
			const res = await fetch("/api/mcp/cowork-status");
			if (!res.ok) return;
			const body = (await res.json()) as { servers: ServerStatus[] };
			this.servers = body.servers;
			// Register a collapsible renderer for every MCP tool name the
			// backend reports. Idempotent — replays on every refresh so we
			// pick up tools that came online after the initial mount.
			for (const s of body.servers) {
				if (s.toolNames?.length) registerMcpRenderers(s.toolNames);
			}
		} catch {
			/* ignore — backend may be restarting */
		}
	}

	private async connect(id: string): Promise<void> {
		this.connectingId = id;
		try {
			const res = await fetch("/api/mcp/cowork-connect", { method: "POST" });
			if (!res.ok) {
				const body = await res.json().catch(() => ({}));
				console.warn("connect failed", body);
				return;
			}
			const body = (await res.json()) as { ok: boolean; authUrl?: string; toolCount?: number };
			if (body.authUrl) {
				// Backend needs the user to authorise. Pop the OAuth tab
				// (Atlassian's consent screen) and poll status in the
				// background — when the callback fires and writes tokens,
				// `hasAtlassianTokens` flips and the pill turns green.
				window.open(body.authUrl, "_blank", "noopener");
				const stopAt = Date.now() + 5 * 60_000;
				const interval = window.setInterval(async () => {
					await this.refresh();
					const stillDisconnected = this.servers.some((s) => s.id === id && !s.connected);
					if (!stillDisconnected || Date.now() > stopAt) {
						window.clearInterval(interval);
					}
				}, 2000);
			}
		} finally {
			this.connectingId = null;
			await this.refresh();
		}
	}

	override render() {
		if (this.servers.length === 0) {
			return html``;
		}
		return html`
			<div class="space-y-1">
				${this.servers.map((s) => this.renderServerRow(s))}
			</div>
		`;
	}

	private renderServerRow(s: ServerStatus) {
		const connecting = this.connectingId === s.id;
		if (s.connected) {
			return html`
				<div class="flex items-center gap-2 px-2 py-1 rounded bg-emerald-50 text-emerald-900 text-xs">
					<span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
					<span class="flex-1 truncate">${s.label}</span>
				</div>
			`;
		}
		return html`
			<div class="flex items-center gap-2 px-2 py-1 rounded bg-amber-50 text-amber-900 text-xs">
				<span class="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
				<span class="flex-1 truncate">${s.label}</span>
				<button
					class="hover:underline disabled:opacity-50"
					?disabled=${connecting}
					@click=${() => this.connect(s.id)}
				>
					${connecting ? "Connecting…" : "Connect"}
				</button>
			</div>
		`;
	}
}
